import { useTransition, animated, useSpring } from "@react-spring/web"
import React, { useCallback, useMemo, useRef, useState } from "react"
import { useBeforeUnload } from "react-use";
import { useLocation, useParams, useNavigate } from 'react-router-dom'

import { useAppState } from "../../providers/app-state";
import MobileNav from "../nav/mobile";
import OrderInfo from "../order-info";
import Sidebar from "./sidebar";

import StepLine from "./step-line";
import { FaHome } from "react-icons/fa";
import { useHttp } from "../../providers/http";

const Stepper = ({ steps, validateOffer = false }) => {
    const { pathname } = useLocation();
    const params = useParams();
    const httpClient = useHttp();
    const navigate = useNavigate();
    const [validated, setValidated] = useState(validateOffer === true ? false : true)

    if (validateOffer) {
        const { shopId, plate, orderId } = params;
        httpClient.get(`check_offer/${shopId}/${orderId}`)
            .then(res => {
                if (!res.data) {
                    setValidated(true)
                    navigate("/faatilbud", { replace: true })
                }
            })
    }

    if (!steps.length) {
        throw new Error("No steps given")
    }

    const { state } = useAppState();

    const filteredSteps = useMemo(() => {
        return steps.filter(step => {
            if (!step["hiddenFn"]) return true;
            return step.hiddenFn(state)
        })
    }, [steps, state])

    const containerRef = useRef()
    const scrollTop = useCallback(() => {
        if (containerRef.current) {
            containerRef.current.scrollTo({
                top: 0,
                behavior: "smooth"
            })
        }
    }, [containerRef])

    const scrollTo = useCallback(pos => {
        if (containerRef.current) {
            containerRef.current.scrollTo({
                top: pos,
                behavior: "smooth"
            })
        }
    }, [containerRef])

    const [activeStep, setActiveStep] = React.useState();
    const [prevStep, setPrevStep] = React.useState(-1);
    const [sidebarObj, setSidebarObj] = useState({
        isOpen: false,
        child: null
    });

    const stepsAmount = useMemo(() => filteredSteps.length, [filteredSteps])

    const isValid = useMemo(() => {
        return filteredSteps[activeStep]?.stepRule && filteredSteps[activeStep].stepRule(state);
    }, [filteredSteps, activeStep, state])

    const buttonSpring = useSpring(
        isValid ? {
            translateY: "0px",
            opacity: 1,
        } : {
            translateY: "300px",
            opacity: 0,
        }
    )

    React.useEffect(() => {
        setActiveStep(0)
    }, [])

    const goNext = () => {
        scrollTop();
        filteredSteps[activeStep].trackerFn && filteredSteps[activeStep].trackerFn(
            activeStep + 1,
            state
        )
        if (activeStep < stepsAmount - 1) {
            setPrevStep(activeStep - 1)
            setActiveStep(activeStep + 1)
        }
    }

    const goPrev = () => {
        scrollTop();
        filteredSteps[activeStep].trackerFn && filteredSteps[activeStep].trackerFn(
            activeStep + 1,
            state,
            "",
            true
        )
        if (activeStep > 0) {
            setPrevStep(activeStep)
            setActiveStep(activeStep - 1)
        }
    }

    const goTo = step => {
        filteredSteps[activeStep].trackerFn && filteredSteps[activeStep].trackerFn(
            activeStep + 1,
            state,
            "",
            true
        )
        setPrevStep(activeStep);
        setActiveStep(step)
    }

    const openSidebar = (el) => {
        setSidebarObj({
            isOpen: true,
            child: el
        })
    }

    const closeSidebar = () => {
        setSidebarObj({
            isOpen: false,
            child: null
        })
    }

    const goToByStepTitle = stepName => {
        const index = filteredSteps.findIndex(item => item.stepName === stepName);
        goTo(index)
    }

    const transition = useTransition
        (
            activeStep,
            activeStep > prevStep
                ? {
                    exitBeforeEnter: true,
                    from: {
                        opacity: 0,
                        transform: `translateX(-100%)`
                    },
                    enter: {
                        opacity: 1,
                        transform: "translateX(0px)"
                    },
                    leave: {
                        opacity: 0,
                        transform: `translateX(50%)`
                    }
                } : {
                    exitBeforeEnter: true,
                    from: {
                        opacity: 0,
                        transform: `translateX(100%)`
                    },
                    enter: {
                        opacity: 1,
                        transform: "translateX(0px)"
                    },
                    leave: {
                        opacity: 0,
                        transform: `translateX(-50%)`
                    }
                }
        )

    useBeforeUnload(activeStep !== 0 && activeStep !== filteredSteps.length - 1, 'Du vil miste dine indtastede oplysninger');

    if (typeof activeStep === "undefined") return null;

    if (!validated) return null

    return (
        <div
            className="min-h-screen max-h-screen overflow-hidden text-skgBlue bg-repair-pattern font-supermolot bg-no-repeat bg-cover"
            style={{
                maxWidth: "100vw",
                overflowX: "hidden"
            }}
        >
            <MobileNav
                backFn={() => {
                    goPrev();
                }}
                lockBackButton={
                    activeStep === filteredSteps.length - 1 || activeStep === 0
                }
                icon={
                    activeStep === 0 ? <FaHome /> : filteredSteps[activeStep - 1].icon
                }
                prevStepTitle={
                    filteredSteps[activeStep > 0 ? activeStep - 1 : 0].stepName
                }
            />
            <animated.div
                className="w-full fixed bottom-0 h-28 flex items-center bg-white justify-center z-10"
                style={{
                    boxShadow: "1px -3px 5px 0px rgba(0,0,0,0.2)",
                    ...buttonSpring
                }}
            >
                <button
                    className="bg-skgBlue px-20 py-5 rounded-md text-white hover:bg-skgRed transition-all"
                    onClick={() => {
                        goNext();
                    }}
                >
                    Fortsæt
                </button>
            </animated.div>
            <div
                className="mx-auto max-w-1400 flex flex-row"
            >
                <div
                    ref={containerRef}
                    className={`basis-full ${activeStep && filteredSteps[activeStep].fullScreen ? "lg:basis-full" : "lg:basis-3/4 lg:pr-16"} overflow-x-hidden overflow-y-auto px-7 pt-6 ${isValid ? "pb-32" : "pb-6"}`}
                    style={{
                        maxHeight: "calc(100vh - 56px)",
                        minHeight: "calc(100vh - 56px)"
                    }}
                >
                    <StepLine
                        steps={filteredSteps}
                        activeStep={activeStep}
                        goTo={goTo}
                    />
                    <a
                        href={process.env.REACT_APP_WEBSITE_URL + "/skader"}
                        className="bg-skgBlue border-l-8 rounded-md border-red-500 text-white py-8 px-4 space-y-3 mb-4 block md:hidden text-center text-xl"
                    >
                        Reparation af skade? <i>Klik her</i>
                    </a>
                    {
                        transition((style, index) => {
                            return (
                                <div>
                                    <animated.div style={style}>
                                        <div
                                            className=""
                                        >
                                            {
                                                React.cloneElement(
                                                    filteredSteps[index].stepElement,
                                                    {
                                                        goNext,
                                                        goPrev,
                                                        goToByStepTitle,
                                                        openSidebar,
                                                        scrollTo,
                                                        pathname,
                                                        forwardTrackerFn: filteredSteps[index].trackerFn,
                                                        index
                                                    }
                                                )
                                            }
                                        </div>
                                    </animated.div>
                                </div>
                            )
                        })
                    }
                </div>
                <div
                    className={`hidden ${activeStep && filteredSteps[activeStep].fullScreen ? "lg:hidden" : "lg:flex"} lg:basis-1/4 sticky top-0 pt-6 pr-4 flex-col justify-between ${isValid ? "pb-32" : "pb-6"}`}
                >
                    <div>
                        <div
                            className="show shadow-lg rounded-md border-l-8 border-skgBlue bg-white mb-4"
                        >
                            <OrderInfo
                                canDelete={filteredSteps[activeStep]?.stepName === "reparationer"}
                            />
                        </div>
                        <a
                            href={process.env.REACT_APP_WEBSITE_URL + "/skader"}
                            className="bg-skgBlue border-l-8 rounded-md border-red-500 text-white py-8 px-4 space-y-3 mb-4 hidden md:block text-center"
                        >
                            <p
                                className="text-3xl mb-8"
                            >
                                Reparation af skade?
                            </p>
                            <a
                                className="text-xl italic"
                            >
                                Klik her
                            </a>
                        </a>
                    </div>
                    <div
                        className="bg-skgBlue border-l-8 rounded-md border-red-500 text-white py-8 px-4 space-y-3 mb-4"
                    >
                        <div>
                            <p
                                className="text-xs font-light"
                            >
                                Opnå en masse fordele med
                            </p>
                            <p
                                className="text-lg"
                            >
                                Mit værksted
                            </p>
                        </div>
                        <div
                            className="text-xs font-light space-y-2"
                        >
                            <div
                                className="flex flex-row items-center space-x-2"
                            >
                                <div
                                    className="rounded-full bg-white h-1.5 w-1.5"
                                />
                                <div>
                                    Hold overblik over dine biler
                                </div>
                            </div>
                            <div
                                className="flex flex-row items-center space-x-2"
                            >
                                <div
                                    className="rounded-full bg-white h-1.5 w-1.5"
                                />
                                <div>
                                    Indhent tilbud hurtigt på dine oprettede biler
                                </div>
                            </div>
                            <div
                                className="flex flex-row items-center space-x-2"
                            >
                                <div
                                    className="rounded-full bg-white h-1.5 w-1.5"
                                />
                                <div>
                                    Foretrukket værksted
                                </div>
                            </div>
                            <div
                                className="flex flex-row items-center space-x-2"
                            >
                                <div
                                    className="rounded-full bg-white h-1.5 w-1.5"
                                />
                                <div>
                                    Se tilbud/ordre/faktura for hver bil
                                </div>
                            </div>
                            <div
                                className="flex flex-row items-center space-x-2"
                            >
                                <div
                                    className="rounded-full bg-white h-1.5 w-1.5"
                                />
                                <div>
                                    Synsrapporter
                                </div>
                            </div>
                            <div
                                className="flex flex-row items-center space-x-2"
                            >
                                <div
                                    className="rounded-full bg-white h-1.5 w-1.5"
                                />
                                <div>
                                    Adgang til fordelagtige tilbud
                                </div>
                            </div>
                        </div>
                        <p
                            className="text-sm"
                        >
                            Psst.... Det 100% gratis at være med i MitVærksted
                        </p>
                    </div>
                </div>
            </div>
            <Sidebar
                {...sidebarObj}
                close={closeSidebar}
            />
        </div>
    )
}

export default Stepper;